var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, FormField, FormGrid, } from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { conversationsApi } from 'apis/conversations';
import { analytics } from 'app/utils/segment';
import { HookFormDateRangePicker, HookFormText, } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useCurrentUser } from 'utils/user/useCurrentUser';
export const ExportConversationsForm = () => {
    const notify = useNotifications();
    const { user: { email }, } = useCurrentUser();
    const [createConversationsCsv] = conversationsApi.useCreateConversationsCsvMutation();
    const form = useForm({
        mode: 'all',
        defaultValues: {
            email: email !== null && email !== void 0 ? email : '',
        },
    });
    const dateRange = useWatch({ control: form.control, name: 'dateRange' });
    const numberOfDays = useMemo(() => {
        if (!(dateRange === null || dateRange === void 0 ? void 0 : dateRange.startDate) || !(dateRange === null || dateRange === void 0 ? void 0 : dateRange.endDate))
            return 0;
        return moment(dateRange.endDate).diff(dateRange.startDate, 'days');
    }, [dateRange]);
    const presets = useMemo(() => {
        const todayStart = moment().startOf('day');
        const todayEnd = moment().endOf('day');
        return [
            {
                key: 'last_7_days',
                label: 'Last 7 days',
                startDate: todayStart.clone().subtract(7, 'days'),
                endDate: todayEnd,
            },
            {
                key: 'last_14_days',
                label: 'Last 14 days',
                startDate: todayStart.clone().subtract(14, 'days'),
                endDate: todayEnd,
            },
            {
                key: 'last_30_days',
                label: 'Last 30 days',
                startDate: todayStart.clone().subtract(30, 'days'),
                endDate: todayEnd,
            },
            {
                key: 'last_90_days',
                label: 'Last 90 days',
                startDate: todayStart.clone().subtract(90, 'days'),
                endDate: todayEnd,
            },
            {
                key: 'last_month',
                label: 'Last month',
                startDate: todayStart.clone().subtract(1, 'month').set('date', 1),
                endDate: todayEnd.clone().set('date', 0),
            },
            {
                key: 'this_month',
                label: 'This month',
                startDate: todayStart.clone().set('date', 1),
                endDate: todayEnd,
            },
        ];
    }, []);
    const handleSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { startDate, endDate } = values.dateRange;
            yield createConversationsCsv({
                email: values.email,
                dateRange: [
                    moment(startDate).format('YYYY-MM-DD'),
                    moment(endDate).format('YYYY-MM-DD'),
                ],
            }).unwrap();
            notify({
                title: 'Conversation export in progress',
                text: `We will send a link to the CSV file to ${values.email}.\n\nIt should only take a few minutes, but in some cases it can take much longer depending on how much data you are exporting.`,
                icon: 'i-done',
                time: 10000,
                showDesktop: false,
            }, NotificationType.Success);
            analytics.analyticsExportMyDataButtonPressed({
                numberOfDays,
            });
        }
        catch (error) {
            notify({
                title: 'Error while exporting the conversations.',
            }, NotificationType.Error);
        }
    });
    return (_jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { name: "email", label: "Send to email", control: form.control, rules: {
                        required: 'Email is required',
                    }, componentProps: {
                        autoFocus: true,
                        type: 'email',
                    } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormDateRangePicker, { name: "dateRange", label: "Date range", control: form.control, rules: {
                        required: 'Date range is required',
                        validate: {
                            tooLongRange: () => numberOfDays <= 90 ||
                                'Choose a date range no longer than 90 days',
                        },
                    }, componentProps: {
                        placeholder: 'Select a date range',
                        dateRangePickerProps: {
                            presets,
                            inputsPlacement: 'top',
                            withDateInput: true,
                        },
                        maxDate: moment().add(1, 'day'),
                    } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(Button, Object.assign({ variant: "primary", disabled: !form.formState.isValid || form.formState.isSubmitting, onClick: form.handleSubmit(handleSubmit) }, { children: "Start export" })) }))] }));
};
